import React from 'react';
import { pluralize } from '../utils/pluralize';

/**
 * AnomalyExclusionTag (Lane 94, extended Lane 96) — inline marker for figures
 * corrected by the bad-order guard (Lane 91, PRICE_ANOMALY_GUARD_ENABLED).
 *
 * The API emits the tag keys — contains_anomaly_exclusion on supplier YoY
 * summary rows, had_anomaly_exclusion on dossier per-material YoY rows — ONLY
 * when the guard flag is ON and an anomalous order was actually excluded from
 * that row's math. When the guard is OFF the keys are absent, `show` is
 * undefined, and this renders nothing, so guard-off responses stay
 * pixel-identical to today.
 *
 * Lane 96 additions (each purely key-presence-conditional):
 *  - `count`: the row's anomaly_excluded_count — the tooltip reads
 *    "Excludes 2 anomalous orders - verify" (singular/plural correct). When
 *    the key is absent the Lane 94 countless wording renders unchanged.
 *  - `exclusions`: the row's anomaly_exclusions detail list — one tooltip
 *    line per excluded order, "excluded order at $56.27 vs typical $0.56".
 *
 * Styling deliberately mirrors the existing IQR outlier marker (the orange
 * asterisk + title tooltip in the same cells); the dagger glyph distinguishes
 * "an order was excluded from this figure" from the asterisk's "unusual mover
 * kept in the math".
 *
 * Linear rebuild Stage 2 (Lane 219, cross-page chrome): this marker renders only
 * inside Linear page scopes (SupplierDetail `.sd` + Analytics `.an`, which both
 * define `--warn`), so the dagger reads the Linear `var(--warn)` amber directly
 * (was the Meridian `colors.warning`) — matching the Lane 215 dossier-child
 * convention and its twin MetricFlagTag, so the two identical daggers in the same
 * cells never render two different ambers.
 */

// Price format matches the surrounding cells' $X.XX; sub-dime prices (the
// $0.0022 gasket class) get four decimals so the tooltip never shows a
// useless "$0.00".
function formatExclusionPrice(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '$--';
  return `$${n >= 0.1 ? n.toFixed(2) : n.toFixed(4)}`;
}

function AnomalyExclusionTag({ show, count, exclusions, title }) {
  if (!show) return null;
  const summary = title || (count
    ? `Excludes ${pluralize(count, 'anomalous order')} - verify`
    : 'Excludes anomalous order(s) - verify');
  const lines = [summary];
  (exclusions || []).forEach((e) => {
    const when = e.po_date ? ` (${e.po_date})` : '';
    lines.push(`excluded order at ${formatExclusionPrice(e.unit_price)} vs typical ${formatExclusionPrice(e.typical_price)}${when}`);
  });
  return (
    <span title={lines.join('\n')} style={{ marginLeft: '4px', color: 'var(--warn)' }}>
      {'†'}
    </span>
  );
}

export default AnomalyExclusionTag;
